/* The DreamCloud dashboard's data, read through the `dreamcloud` edge function.
 *
 * The browser never holds a DreamCloud key: the function checks who is asking
 * (owner or platform admin, and licensed), reads the key from Vault, and
 * forwards only the routes in its ACTIONS table. What is here is wiring; the
 * arithmetic and wording are in js/rules/dreamcloud.js.
 *
 * Online-only, like js/members.js and for the same reason: nothing here is a
 * fact that happened at the front desk, so it is not queued. A failed refresh
 * keeps what is already on screen and says so (`error`, and `lastOkAt` that
 * stops moving) rather than blanking numbers an owner may be reading.
 */
import * as Vue from 'vue';
import { sync } from './sync.js';
import { tenant } from './tenant.js';

var FUNCTION = 'dreamcloud';
var POLL_MS = 60000;
// A hung edge call must not leave `loading` true for ever.
var LOAD_TIMEOUT_MS = 30000;

var state = Vue.reactive({
  range: '7',
  summary: null,
  problems: null,
  jobs: null,
  loading: false,
  error: null,
  lastOkAt: null,
  // 409: the salon has no DreamCloud connection yet. Not a fault, so no error.
  notConnected: false,
  // 403: not an owner, or the licence does not include DreamCloud.
  forbidden: null,
  // Writes (a replay send, a call report store, an Apply) still on their way.
  // While one is, the page keeps the Actions section mounted.
  writing: 0,
  // The last write's outcome, { ok, text, at }, kept here rather than in the
  // dialog so it is still there if the owner left the page during the send.
  lastWrite: null
});

var FORBIDDEN = 'DreamCloud is for salon owners on a plan that includes it.';

/* One call to the edge function.
 *
 * Same digging as members.js: supabase-js reports a non-2xx with the body
 * unread, and the sentence the function wrote is the useful half. The status
 * rides along on the thrown error because the caller reacts to 403 and 409
 * differently from anything else.
 */
async function invoke(action, params) {
  // Dev builds only: Vite replaces import.meta.env.DEV with false in a
  // production build, so this branch and the fixtures import are dropped.
  if (import.meta.env.DEV && mockMode()) {
    var fx = await import('./rules/dreamcloud.fixtures.js');
    return fx.mockResponse(action, params || {}, mockMode());
  }
  var res = await sync.sb.functions.invoke(FUNCTION, {
    body: { action: action, params: params || {}, tenant_id: tenant.get() }
  });
  if (res.error) {
    var said = null;
    var status = null;
    var body = null;
    try {
      var ctx = res.error.context;
      if (ctx) {
        status = ctx.status || null;
        if (typeof ctx.json === 'function') {
          body = await ctx.json();
          said = (body && body.error) || null;
        }
      }
    } catch (e) { /* not json, or already consumed */ }
    var err = new Error(said || res.error.message || 'That did not work.');
    err.status = status;
    // The function's own sentence, apart from supabase-js's generic one.
    err.said = said;
    // The whole answer: a write that partly happened (audience sync's 502
    // carries its counts and errors[]) still says what it did.
    err.body = body;
    throw err;
  }
  return res.data;
}

/* `?dcmock=1` (or =clear, =error, =409, =403) answers from
 * js/rules/dreamcloud.fixtures.js instead of the edge function, so the page
 * can be looked at without a worker. Dev builds only (see invoke). The mode
 * can be changed afterwards from the browser console through
 * `window.__dcmock.mode`, e.g. to make a later refresh fail. */
function mockMode() {
  if (typeof window === 'undefined' || !window.location) return null;
  if (!window.__dcmock) {
    var q = new URLSearchParams(window.location.search).get('dcmock');
    if (!q) return null;
    window.__dcmock = { mode: q === '1' ? 'full' : q, state: state };
  }
  return window.__dcmock.mode;
}

function withTimeout(promise) {
  var t;
  var limit = new Promise(function (resolve, reject) {
    t = setTimeout(function () {
      reject(new Error('DreamCloud took too long to answer.'));
    }, LOAD_TIMEOUT_MS);
  });
  return Promise.race([promise, limit]).finally(function () { clearTimeout(t); });
}

/* Each load is numbered. Only the newest may write: if the range changes while
 * a load is in flight, the old response describes the wrong range and is
 * dropped, and setRange has already started the load that replaces it.
 *
 * Fetch the three panels together. Any one failing fails the refresh as a
 * whole, so the panels never show different moments. */
var seq = 0;

async function load() {
  var mine = ++seq;
  state.loading = true;
  try {
    var got = await withTimeout(Promise.all([
      invoke('summary', { range: state.range }),
      invoke('problems'),
      invoke('jobs')
    ]));
    if (mine !== seq) return;
    state.summary = got[0];
    state.problems = got[1];
    state.jobs = got[2];
    state.lastOkAt = new Date();
    state.error = null;
    state.notConnected = false;
    state.forbidden = null;
  } catch (err) {
    if (mine !== seq) return;
    // Previous data and lastOkAt are left alone on purpose.
    if (err.status === 409) {
      state.notConnected = true;
      state.error = null;
      state.forbidden = null;
    } else if (err.status === 403) {
      state.forbidden = err.said || FORBIDDEN;
      state.notConnected = false;
      state.error = null;
    } else {
      state.error = String(err.message);
      state.notConnected = false;
      state.forbidden = null;
    }
  } finally {
    if (mine === seq) state.loading = false;
  }
}

function setRange(r) {
  state.range = String(r);
  return load();
}

/* Poll while the module is on screen, and not while the tab is hidden: a
 * background tab would otherwise keep spending worker and database reads on
 * numbers nobody can see. The tick is skipped, not cancelled, so the first
 * tick after the tab returns refreshes. */
var timer = null;

function startPolling() {
  if (timer) return;
  timer = setInterval(function () {
    if (typeof document !== 'undefined' && document.hidden) return;
    if (state.loading) return;
    // A 403 or a missing connection will not mend itself in a minute; the
    // page's load on the next visit asks again.
    if (state.forbidden || state.notConnected) return;
    load();
  }, POLL_MS);
}

function stopPolling() {
  if (timer) clearInterval(timer);
  timer = null;
}

/* Closing or reloading the tab mid-write would lose the outcome (and, for a
 * replay, stop the batches after the current one), so while any write is on
 * its way the browser asks first. Added on the first write, removed when the
 * last one ends; never left behind to nag about a page with nothing running. */
function guardUnload(e) {
  e.preventDefault();
  e.returnValue = '';
}

function setUnloadGuard(on) {
  if (typeof window === 'undefined' || !window.addEventListener) return;
  if (on) window.addEventListener('beforeunload', guardUnload);
  else window.removeEventListener('beforeunload', guardUnload);
}

function beginWrite() {
  state.writing++;
  if (state.writing === 1) setUnloadGuard(true);
}

function endWrite(outcome) {
  var was = state.writing;
  state.writing = Math.max(0, state.writing - 1);
  if (was > 0 && state.writing === 0) setUnloadGuard(false);
  if (outcome) state.lastWrite = Object.assign({ at: new Date() }, outcome);
}

export const dreamcloud = {
  state: state,
  load: load,
  setRange: setRange,
  invoke: invoke,
  startPolling: startPolling,
  stopPolling: stopPolling,
  beginWrite: beginWrite,
  endWrite: endWrite
};
